import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Callout,
  EmptyState,
  IconButton,
  Paginator,
  Spinner,
  Table,
  Tag,
  TextField,
  Tooltip,
  type TableColumn,
} from '@humanforce/mango-ui-react';
import type {
  PayRunPopulationCheckResult,
  PayRunPopulationEmployee,
  PayRunPopulationPage,
  PayRunPopulationRefreshAccepted,
  PopulationLoadStatus,
} from './payRunPopulationApiTypes';
import type { PayRunCalculationStatus } from './payRunsApiTypes';
import {
  checkPopulationChanges,
  getPopulation,
  refreshPopulation,
} from './payRunPopulationHttpClient';
import './PayRunEmployeesTab.css';

interface PayRunEmployeesTabProps {
  payId: string;
  workflowStatus: string;
  calculationStatus?: PayRunCalculationStatus;
  populationStatus: PopulationLoadStatus | null | undefined;
  /** Reason reported by the API for a failed load (for example a queued HR API failure). */
  populationFailure?: { code?: string | null; detail?: string | null } | null;
  canRefreshPopulation: boolean;
  onRefreshAccepted?: (accepted: PayRunPopulationRefreshAccepted) => void;
}

const pageSize = 20;
const unlockedStatuses = new Set(['open', 'compliance']);
const currency = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' });
const number = new Intl.NumberFormat('en-AU', { maximumFractionDigits: 2 });

const words = (value: string): string => value
  .split('_')
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
  .join(' ');

const lockedStatusLabel = (value: string): string =>
  value === 'ready_to_pay' ? 'Ready to Pay' : words(value);

const TruncatedText = ({ text, strong = false }: { text: string; strong?: boolean }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [truncated, setTruncated] = useState(false);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    const measure = () => setTruncated(element.scrollWidth > element.offsetWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [text]);

  const content = (
    <span ref={ref} className={`pay-run-employee-text mgo-truncate${strong ? ' mgo-weight-strong' : ''}`}>
      {text}
    </span>
  );

  return truncated ? <Tooltip content={text}>{content}</Tooltip> : content;
};

export const PayRunEmployeesTab = ({
  payId,
  workflowStatus,
  calculationStatus = 'not_ready',
  populationStatus,
  populationFailure,
  canRefreshPopulation,
  onRefreshAccepted,
}: PayRunEmployeesTabProps) => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [result, setResult] = useState<PayRunPopulationPage | null>(null);
  const [change, setChange] = useState<PayRunPopulationCheckResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(true);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      setResult(await getPopulation(payId, { page, pageSize, search }, signal));
    } catch (loadError) {
      if (!signal?.aborted) {
        setError(loadError instanceof Error ? loadError.message : 'Employees could not be loaded.');
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [page, payId, search]);

  const check = useCallback(async (signal?: AbortSignal) => {
    setChecking(true);
    try {
      setChange(await checkPopulationChanges(payId, signal));
    } catch {
      if (!signal?.aborted) setChange(null);
    } finally {
      if (!signal?.aborted) setChecking(false);
    }
  }, [payId]);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load, populationStatus]);

  useEffect(() => {
    const controller = new AbortController();
    void check(controller.signal);
    return () => controller.abort();
  }, [check, populationStatus]);

  const columns = useMemo<TableColumn[]>(() => {
    const calculated = calculationStatus === 'complete';
    const calculatedMoney = (value: number) => calculated && Number.isFinite(value) ? currency.format(value) : '—';
    const columnPresentation = (width: string) => ({
      bodyClassName: 'pay-run-employee-table__cell',
      headerClassName: 'pay-run-employee-table__header',
      style: { width, minWidth: width, maxWidth: width },
    });
    return [
    {
      field: 'displayName',
      header: 'Employee',
      ...columnPresentation('15.125rem'),
      body: (employee: PayRunPopulationEmployee) => (
        <span className="pay-run-employee-identity mgo-stack mgo-gap-0 mgo-text-xs">
          <TruncatedText text={employee.displayName} strong />
          <span className="mgo-color-muted"><TruncatedText text={employee.employeeNumber} /></span>
        </span>
      ),
    },
    { field: 'roleTitle', header: 'Role', ...columnPresentation('8.8125rem'), body: (employee: PayRunPopulationEmployee) => <TruncatedText text={employee.roleTitle || '—'} /> },
    { field: 'units', header: 'Units', ...columnPresentation('4.125rem'), body: (employee: PayRunPopulationEmployee) => number.format(employee.units) },
    { field: 'hourlyUnits', header: 'Hourly units', ...columnPresentation('6.125rem'), body: (employee: PayRunPopulationEmployee) => number.format(employee.hourlyUnits) },
    { field: 'gross', header: 'Gross', ...columnPresentation('4.875rem'), body: (employee: PayRunPopulationEmployee) => calculatedMoney(employee.gross) },
    { field: 'taxable', header: 'Taxable', ...columnPresentation('4.3125rem'), body: (employee: PayRunPopulationEmployee) => calculatedMoney(employee.taxable) },
    { field: 'tax', header: 'Tax', ...columnPresentation('4.0625rem'), body: (employee: PayRunPopulationEmployee) => calculatedMoney(employee.tax) },
    { field: 'stsl', header: 'STSL', ...columnPresentation('3.9375rem'), body: (employee: PayRunPopulationEmployee) => calculatedMoney(employee.stsl) },
    { field: 'btd', header: 'BTD', ...columnPresentation('3.9375rem'), body: () => '—' },
    { field: 'super', header: 'Super', ...columnPresentation('3.9375rem'), body: (employee: PayRunPopulationEmployee) => calculatedMoney(employee.super) },
    { field: 'atd', header: 'ATD', ...columnPresentation('4.625rem'), body: () => '—' },
    { field: 'netPay', header: 'Net pay', ...columnPresentation('5.375rem'), body: (employee: PayRunPopulationEmployee) => calculatedMoney(employee.netPay) },
    {
      field: 'exceptionSeverity',
      header: 'Status',
      ...columnPresentation('9.6875rem'),
      body: (employee: PayRunPopulationEmployee) => employee.exceptionSeverity
        ? <Tag label={employee.exceptionSeverity === 'blocking' ? 'Failed' : '1 issue'} tone={employee.exceptionSeverity === 'blocking' ? 'critical' : 'caution'} />
        : <Tag label="Clear" tone="positive" />,
    },
    {
      field: 'actions',
      header: '',
      ...columnPresentation('3.6875rem'),
      body: (employee: PayRunPopulationEmployee) => (
        <IconButton
          icon="more"
          label={`Open ${employee.displayName}`}
          variant="tertiary"
          size="small"
          href={`/workers/worker-record/personal?worker=${encodeURIComponent(employee.personId)}`}
        />
      ),
    },
    ];
  }, [calculationStatus]);

  const apply = async () => {
    setApplying(true);
    setActionError(null);
    try {
      const accepted = await refreshPopulation(payId);
      onRefreshAccepted?.(accepted);
    } catch (refreshError) {
      setActionError(refreshError instanceof Error ? refreshError.message : 'Employee changes could not be applied.');
    } finally {
      setApplying(false);
    }
  };

  const unlocked = unlockedStatuses.has(workflowStatus);
  const hasActivePopulation = result?.revisionNumber != null;
  const showAction = canRefreshPopulation && unlocked && !checking && !applying
    && populationStatus !== 'loading'
    && (!hasActivePopulation || change?.changeAvailable === true);

  return (
    <section aria-label="Employee population" className="mgo-stack mgo-gap-l" aria-busy={loading || applying}>
      <div className="mgo-split mgo-align-center mgo-gap-s pay-run-employee-toolbar">
        <div className="pay-run-employee-toolbar__search">
          <TextField
            label="Search employees"
            hideLabel
            placeholder="Search employees..."
            leadingIcon="search"
            clearable
            value={search}
            onChange={(event) => { setSearch(event.target.value); setPage(1); }}
            onClear={() => { setSearch(''); setPage(1); }}
          />
        </div>
        {result && (
          <span className="mgo-text-xs mgo-color-muted">
            {number.format(result.employeeCount)} {result.employeeCount === 1 ? 'employee' : 'employees'}
          </span>
        )}
      </div>

      {populationStatus === 'loading' && (
        <Callout tone="info" title="Applying employee changes">
          <span className="mgo-cluster mgo-gap-s"><Spinner /> A refreshed employee snapshot is being prepared.</span>
        </Callout>
      )}

      {populationStatus === 'failed' && (
        <Callout tone="critical" title="Employee load failed">
          <span>{populationFailure?.detail ?? 'The employee population could not be loaded.'}</span>
          {populationFailure?.code && <span className="mgo-text-xs mgo-color-muted"> Code: {populationFailure.code}</span>}
        </Callout>
      )}

      {change?.sourceComparable === false && (
        <Callout tone="info" title="Employee changes are not tracked for this population">
          This population came from a different source than the one currently configured, so upstream changes cannot be
          detected for it.
        </Callout>
      )}

      {change?.changeAvailable && (
        <Callout
          tone="caution"
          title="Employee changes available"
          action={showAction
            ? <Button label="Apply employee changes" icon="refresh" loading={applying} onClick={() => void apply()} />
            : undefined}
        >
          {unlocked
            ? canRefreshPopulation
              ? `${change.uniquePersonCount} employees are available in the latest HR snapshot.`
              : `${change.uniquePersonCount} employees are available; a Payroll Manager can apply them.`
            : `Changes cannot be applied because this Pay Run is locked at ${lockedStatusLabel(workflowStatus)}.`}
        </Callout>
      )}

      {actionError && <Callout tone="critical" title="Employee changes not applied">{actionError}</Callout>}
      {error && (
        <Callout
          tone="critical"
          title="Employees unavailable"
          action={<Button label="Retry" variant="secondary" size="small" onClick={() => void load()} />}
        >
          {error}
        </Callout>
      )}

      {loading && !error ? (
        <div className="mgo-cluster mgo-justify-center mgo-gap-s" style={{ minHeight: '12rem' }}>
          <Spinner /> <span>Loading employees…</span>
        </div>
      ) : !error && !hasActivePopulation ? (
        <EmptyState
          variant="no-data"
          title="No employee population loaded"
          description={canRefreshPopulation
            ? 'Choose the employee and Timesheet sources for this Pay Run.'
            : 'A Payroll Manager must load the employee population for this Pay Run.'}
        />
      ) : !error && result && result.items.length === 0 ? (
        <EmptyState
          variant="no-data"
          title="No employees match your search"
          description="Change or clear the current search."
          actions={[<Button key="clear" label="Clear search" variant="secondary" onClick={() => { setSearch(''); setPage(1); }} />]}
        />
      ) : !error && result ? (
        <div style={{ overflowX: 'auto' }}>
          <Table<PayRunPopulationEmployee>
            value={result.items}
            columns={columns}
            scrollable
            className="pay-run-employee-table"
            ptProps={{
              rowClassName: (employee) => ({
                'pay-run-table-row--critical': employee.exceptionSeverity === 'blocking',
                'pay-run-table-row--caution': Boolean(employee.exceptionSeverity && employee.exceptionSeverity !== 'blocking'),
              }),
              tableStyle: { minWidth: '82.625rem', width: '100%', tableLayout: 'fixed' },
            }}
            emptyMessage="No employees."
          />
        </div>
      ) : null}

      {!loading && !error && result && result.pagination.totalPages > 1 && <Paginator first={(page - 1) * pageSize}
        itemsPerPage={pageSize} totalRecords={result.pagination.totalItems}
        onPageChange={(event) => setPage(event.page + 1)} />}
    </section>
  );
};
